/* views/SimpleViews.jsx
 * ProductionView, InventoryView, ProductsView, ExpensesView, UsersView
 * Data: real Supabase via db.jsx domain helpers
 * Design: v6
 */

import React from 'react'
import { humanize, formatMoney, formatDate, formatDateTime, ENUM_OPTIONS, UTENSILS_CATEGORY_ID } from '../utils.jsx'
import { Production, Inventory, Products, Expenses, Orders, Contacts, Users, AuditLog, rpc, update } from '../db.jsx'
import { Pill, Drawer, ConfirmModal, Field, EnumSelect, EmptyState, Spinner, useToast } from '../components.jsx'
import { I } from '../icons.jsx'
import { useAuth } from '../auth.jsx'
import { reportError } from '../errors.jsx'

/* ============================================================
 * PRODUCTION VIEW
 * ============================================================ */
const BATCH_STATUS = ["pending", "in_progress", "completed", "cancelled"];
const ING_SECTIONS = [
  { key: 'base_flan', label: 'Base Flan' },
  { key: 'topping',   label: 'Toppings' },
  { key: 'packaging', label: 'Packaging' },
];

function batchStatusTone(s) {
  switch (s) {
    case "completed": return "ok";
    case "in_progress": return "info";
    case "pending": return "warn";
    case "cancelled": return "muted";
    default: return "muted";
  }
}

function dispositionPill(b) {
  const d = b.disposition || 'pending';
  if (d === 'order')  return <Pill tone="ok"><span className="dot"/>Order{b.order_number ? ` #${b.order_number}` : ''}</Pill>;
  if (d === 'waste')  return <Pill tone="danger"><span className="dot"/>Waste</Pill>;
  if (d === 'sample') return <Pill tone="muted"><span className="dot"/>Sample</Pill>;
  if (d === 'other')  return <Pill tone="muted"><span className="dot"/>Other</Pill>;
  return <Pill tone="warn"><span className="dot"/>Unlinked</Pill>;
}

function ProductionView() {
  const toast = useToast();
  const [batches, setBatches] = React.useState(null);
  const [costing, setCosting] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [open, setOpen] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [tab, setTab] = React.useState("batches");
  const [filters, setFilters] = React.useState({ q: "", product_id: "", from: "", to: "" });

  React.useEffect(() => {
    const ctrl = new AbortController();
    Promise.all([
      Production.batches(ctrl.signal),
      Production.costing(ctrl.signal),
      Products.list(ctrl.signal),
    ]).then(([b, c, p]) => {
      if (ctrl.signal.aborted) return;
      setBatches(Array.isArray(b) ? b : (b.data || []));
      setCosting(Array.isArray(c) ? c : (c.data || []));
      setProducts(Array.isArray(p) ? p : (p.data || []));
    }).catch(() => { setBatches([]); });
    return () => ctrl.abort();
  }, []);

  const refresh = () => {
    Production.batches().then(r => setBatches(Array.isArray(r) ? r : (r.data || [])));
    Production.costing().then(r => setCosting(Array.isArray(r) ? r : (r.data || [])));
  };

  const filtered = (batches || []).filter(b => {
    if (filters.q) {
      const q = filters.q.toLowerCase();
      if (!(b.product_name_snapshot || "").toLowerCase().includes(q) &&
          !(b.flavors || "").toLowerCase().includes(q) &&
          !String(b.order_number || "").includes(q)) return false;
    }
    if (filters.product_id && b.product_id !== filters.product_id) return false;
    if (filters.from && (b.date_made || "").slice(0, 10) < filters.from) return false;
    if (filters.to && (b.date_made || "").slice(0, 10) > filters.to) return false;
    return true;
  });

  async function updateBatchStatus(id, status) {
    await Production.updateStatus(id, status);
    toast.push({ tone: "ok", title: "Status updated", msg: humanize(status) });
    refresh();
  }

  return (
    <div className="view-enter">
      <div className="tabs" style={{ marginBottom: 14 }}>
        <button className={tab === "batches" ? "active" : ""} onClick={() => setTab("batches")}>Batches</button>
        <button className={tab === "costing" ? "active" : ""} onClick={() => setTab("costing")}>Costing &amp; margin</button>
      </div>

      {tab === "batches" && (
        <>
          <div className="filter-bar">
            <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 260px", minWidth: 180 }}>
              <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
              <input placeholder="Search product or flavors…" value={filters.q} onChange={e => setFilters({ ...filters, q: e.target.value })} />
            </div>
            <select className="select" style={{ width: "auto", minWidth: 180 }}
                    value={filters.product_id} onChange={e => setFilters({ ...filters, product_id: e.target.value })}>
              <option value="">All products</option>
              {products.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <span className="spacer"/>
            <button className="btn btn-primary" onClick={() => setShowNew(true)}>{I.plus} New batch</button>
          </div>

          <div className="card">
            <div className="tbl-wrap">
              {batches === null
                ? <Spinner label="Loading batches…" />
                : filtered.length === 0
                  ? <EmptyState title="No batches" hint={batches.length ? "No batches match your filters." : "Start tracking production."} />
                  : <table className="tbl tbl-production">
                      <thead><tr>
                        <th>Date</th><th>Product</th>
                        <th>Status</th><th>Unit</th><th>Disposition</th>
                      </tr></thead>
                      <tbody>
                        {filtered.map(b => (
                          <tr key={b.id} onClick={() => setOpen(b)} className={open?.id === b.id ? "selected" : ""}>
                            <td className="muted">{formatDate(b.date_made)}</td>
                            <td style={{ fontWeight: 600 }}>{b.product_name_snapshot}</td>
                            <td><Pill tone={batchStatusTone(b.status)}><span className="dot"/>{humanize(b.status)}</Pill></td>
                            <td className="muted">{b.unit || "tin (6x2)"}</td>
                            <td>{dispositionPill(b)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
              }
            </div>
          </div>
        </>
      )}

      {tab === "costing" && (
        <div className="card">
          <div className="tbl-wrap">
            {costing.length === 0
              ? <EmptyState title="No costing data" hint="Production batches with ingredients will appear here." />
              : <table className="tbl">
                  <thead><tr>
                    <th>Date</th><th>Product</th>
                    <th style={{ textAlign: "right" }}>Qty</th>
                    <th style={{ textAlign: "right" }}>Retail</th>
                    <th style={{ textAlign: "right" }}>COGS/unit</th>
                    <th style={{ textAlign: "right" }}>Margin/unit</th>
                    <th style={{ textAlign: "right" }}>Margin %</th>
                  </tr></thead>
                  <tbody>
                    {costing.map(b => {
                      const marginPct = b.margin_pct || 0;
                      const tone = marginPct > 30 ? "ok" : marginPct > 10 ? "warn" : "danger";
                      return (
                        <tr key={b.batch_id} style={{ cursor: "default" }}>
                          <td className="muted">{formatDate(b.date_made)}</td>
                          <td style={{ fontWeight: 600 }}>{b.product_name_snapshot}</td>
                          <td className="mono" style={{ textAlign: "right" }}>{b.qty_produced}</td>
                          <td className="amt" style={{ textAlign: "right" }}>{formatMoney(b.retail_price)}</td>
                          <td className="amt" style={{ textAlign: "right" }}>{formatMoney(b.cogs_per_unit)}</td>
                          <td className="amt" style={{ textAlign: "right" }}>{formatMoney(b.gross_margin_per_unit)}</td>
                          <td style={{ textAlign: "right" }}><Pill tone={tone}>{Math.round(marginPct)}%</Pill></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
            }
          </div>
        </div>
      )}

      {open && (
        <BatchDrawer
          batch={open}
          onClose={() => setOpen(null)}
          onStatusChange={(s) => updateBatchStatus(open.id, s)}
          onRefresh={() => { setOpen(null); refresh(); }}
        />
      )}
      {showNew && <NewBatchDrawer products={products} onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); refresh(); }} />}
    </div>
  );
}

function BatchDrawer({ batch, onClose, onStatusChange, onRefresh }) {
  const toast = useToast();
  const [ingredients, setIngredients] = React.useState([]);
  const [editIng, setEditIng] = React.useState(null);
  const [showAddIng, setShowAddIng] = React.useState(false);
  const [addIngSection, setAddIngSection] = React.useState('base_flan');
  const [addForm, setAddForm] = React.useState({ item_id: '', qty_used: '', unit: '', notes: '', ingredient_type: 'base_flan' });
  const [items, setItems] = React.useState([]);
  const [addBusy, setAddBusy] = React.useState(false);
  const [editingMeta, setEditingMeta] = React.useState(false);
  const [metaForm, setMetaForm] = React.useState({ flavors: batch.flavors || "", date_made: batch.date_made || "" });
  const [batchFlavorSets, setBatchFlavorSets] = React.useState([]);
  const [showLinkOrder, setShowLinkOrder] = React.useState(false);
  const [showMarkWaste, setShowMarkWaste] = React.useState(false);
  const [disposition, setDisposition] = React.useState(batch.disposition || 'pending');
  const [linkedOrderNumber, setLinkedOrderNumber] = React.useState(batch.order_number || null);
  const [confirmRemoveIngId, setConfirmRemoveIngId] = React.useState(null);
  const [confirmDeleteBatch, setConfirmDeleteBatch] = React.useState(false);

  React.useEffect(() => {
    if (batch.product_id)
      Products.flavorSets(batch.product_id).then(r => setBatchFlavorSets((Array.isArray(r) ? r : (r.data || [])).filter(s => s.active)));
  }, [batch.product_id]);

  const loadIngredients = React.useCallback(() => {
    Production.ingredients(batch.id).then(r => setIngredients(Array.isArray(r) ? r : (r.data || [])));
  }, [batch.id]);

  React.useEffect(() => { loadIngredients(); }, [loadIngredients]);

  React.useEffect(() => {
    if (showAddIng && items.length === 0)
      Production.items().then(r => setItems(Array.isArray(r) ? r : (r.data || [])));
  }, [showAddIng]);

  async function saveEditIng() {
    if (!editIng || !editIng.qty_used) return;
    const res = await Production.updateIngredient(editIng.id, Number(editIng.qty_used), editIng.unit, editIng.notes || '');
    if (!res.error) { setEditIng(null); toast.push({ tone: 'ok', title: 'Ingredient updated' }); loadIngredients(); }
  }

  async function doRemoveIngredient(id) {
    await Production.removeIngredient(id);
    toast.push({ tone: "ok", title: "Ingredient removed" });
    loadIngredients();
  }

  async function doDeleteBatch() {
    const res = await Production.remove(batch.id);
    if (!res.error) {
      toast.push({ tone: 'ok', title: 'Batch deleted' });
      if (onRefresh) onRefresh();
    } else {
      toast.push({ tone: 'danger', title: 'Failed to delete batch' });
    }
  }

  function openAddIng(sectionKey) {
    setAddIngSection(sectionKey);
    setAddForm({ item_id: '', qty_used: '', unit: '', notes: '', ingredient_type: sectionKey });
    setShowAddIng(true);
  }

  async function addIngredient() {
    if (!addForm.item_id || !addForm.qty_used) return;
    setAddBusy(true);
    const res = await Production.addIngredient(batch.id, addForm.item_id, Number(addForm.qty_used), addForm.unit, addForm.notes || null, null, addForm.ingredient_type);
    setAddBusy(false);
    if (!res.error) {
      setAddForm({ item_id: '', qty_used: '', unit: '', notes: '', ingredient_type: addIngSection });
      setShowAddIng(false);
      toast.push({ tone: 'ok', title: 'Ingredient added' });
      loadIngredients();
    }
  }

  function pickItem(id) {
    const it = items.find(x => x.id === id);
    setAddForm(p => ({ ...p, item_id: id, unit: it?.default_unit || p.unit }));
  }

  async function saveMeta() {
    const res = await Production.update(batch.id, {
      p_date_made: metaForm.date_made, p_flavors: metaForm.flavors || null,
      p_qty_produced: batch.qty_produced, p_unit: batch.unit, p_notes: batch.notes || null,
    });
    if (!res.error) { toast.push({ tone: "ok", title: "Batch updated" }); setEditingMeta(false); onClose(); }
  }

  async function handleLinkOrder(orderId, orderNumber) {
    const res = await Production.linkToOrder(batch.id, orderId);
    if (!res.error) {
      setDisposition('order');
      setLinkedOrderNumber(orderNumber);
      setShowLinkOrder(false);
      toast.push({ tone: 'ok', title: `Linked to Order #${orderNumber}` });
      if (onRefresh) onRefresh();
    } else {
      toast.push({ tone: 'danger', title: 'Failed to link order' });
    }
  }

  async function handleMarkWaste(reason) {
    const res = await Production.markWaste(batch.id, reason);
    if (!res.error) {
      setDisposition('waste');
      setShowMarkWaste(false);
      toast.push({ tone: 'ok', title: 'Marked as waste' });
      if (onRefresh) onRefresh();
    } else {
      toast.push({ tone: 'danger', title: 'Failed to mark waste' });
    }
  }

  function IngredientRow({ i }) {
    const isEditing = editIng?.id === i.id;
    if (isEditing) return (
      <tr key={i.id}>
        <td style={{ fontWeight: 600 }}>{i.item_name}</td>
        <td className="mono" style={{ textAlign: "right" }}>{i.qty_standard != null ? i.qty_standard : <span className="faint">—</span>}</td>
        <td><input className="input" type="number" step="0.001" value={editIng.qty_used} style={{ width: 80 }} onChange={e => setEditIng({ ...editIng, qty_used: e.target.value })} /></td>
        <td></td>
        <td><input className="input" value={editIng.unit} style={{ width: 70 }} onChange={e => setEditIng({ ...editIng, unit: e.target.value })} /></td>
        <td style={{ textAlign: 'right' }}>
          <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
            <button className="btn btn-primary btn-sm" onClick={saveEditIng}>{I.check}</button>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditIng(null)}>{I.x}</button>
          </div>
        </td>
      </tr>
    );
    const v = i.qty_standard != null ? i.qty_used - i.qty_standard : null;
    return (
      <tr key={i.id} style={{ cursor: "default" }}>
        <td style={{ fontWeight: 600 }}>{i.item_name}</td>
        <td className="mono" style={{ textAlign: "right" }}>{i.qty_standard != null ? i.qty_standard : <span className="faint">—</span>}</td>
        <td className="mono" style={{ textAlign: "right" }}>{i.qty_used}</td>
        <td className="mono" style={{ textAlign: "right" }}>
          {v != null ? <span style={{ color: Math.abs(v) < 0.001 ? "var(--text-faint)" : v > 0 ? "var(--warn)" : "var(--ok)" }}>{v > 0 ? "+" : ""}{v.toFixed(3)}</span> : <span className="faint">—</span>}
        </td>
        <td className="muted">{i.unit}</td>
        <td style={{ textAlign: "right" }}>
          <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditIng({ id: i.id, qty_used: String(i.qty_used), unit: i.unit || '', notes: i.notes || '' })}>{I.edit}</button>
            <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemoveIngId(i.id)}>{I.trash}</button>
          </div>
        </td>
      </tr>
    );
  }

  function IngSection({ sectionKey, label }) {
    const rows = ingredients.filter(i => i.ingredient_type === sectionKey);
    const isAdding = showAddIng && addIngSection === sectionKey;
    return (
      <div style={{ marginBottom: 18 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-faint)", fontWeight: 600 }}>{label}</div>
          <button className="btn btn-ghost btn-sm" onClick={() => isAdding ? setShowAddIng(false) : openAddIng(sectionKey)}>{isAdding ? I.x : <>{I.plus} Add</>}</button>
        </div>
        {rows.length > 0 && (
          <table className="tbl">
            <thead><tr>
              <th>Item</th>
              <th style={{ textAlign: "right" }}>Std</th>
              <th style={{ textAlign: "right" }}>Used</th>
              <th style={{ textAlign: "right" }}>Var</th>
              <th>Unit</th><th></th>
            </tr></thead>
            <tbody>{rows.map(i => <IngredientRow key={i.id} i={i} />)}</tbody>
          </table>
        )}
        {isAdding && (
          <div style={{ marginTop: 8, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px dashed var(--border-strong)" }}>
            <Field label="Item">
              <select className="select" value={addForm.item_id} onChange={e => pickItem(e.target.value)}>
                <option value="">— Select item —</option>
                {items.map(it => <option key={it.id} value={it.id}>{it.name}</option>)}
              </select>
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
              <Field label="Qty used"><input className="input" type="number" step="0.001" value={addForm.qty_used} onChange={e => setAddForm(p => ({ ...p, qty_used: e.target.value }))} /></Field>
              <Field label="Unit"><input className="input" value={addForm.unit} onChange={e => setAddForm(p => ({ ...p, unit: e.target.value }))} /></Field>
            </div>
            <Field label="Notes"><input className="input" value={addForm.notes} onChange={e => setAddForm(p => ({ ...p, notes: e.target.value }))} /></Field>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
              <button className="btn btn-ghost btn-sm" onClick={() => setShowAddIng(false)}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={addIngredient} disabled={addBusy || !addForm.item_id || !addForm.qty_used}>{I.plus} Add</button>
            </div>
          </div>
        )}
        {rows.length === 0 && !isAdding && (
          <div style={{ fontSize: 12, color: "var(--text-faint)", padding: "6px 0 2px" }}>No {label.toLowerCase()} logged.</div>
        )}
      </div>
    );
  }

  const dispositionColor = disposition === 'order' ? 'var(--ok)' : disposition === 'waste' ? 'var(--danger)' : 'var(--warn)';

  return (
    <Drawer
      title={<>{batch.product_name_snapshot}<Pill tone={batchStatusTone(batch.status)} style={{ marginLeft: 8 }}><span className="dot"/>{humanize(batch.status)}</Pill></>}
      subtitle={`${formatDate(batch.date_made)} · 1 ${batch.unit || "tin"}`}
      onClose={onClose}
      footer={<><button className="btn btn-danger" onClick={() => setConfirmDeleteBatch(true)}>{I.trash} Delete</button><span className="spacer"/><button className="btn btn-ghost" onClick={onClose}>Close</button></>}
    >
      <Field label="Status">
        <select className="select" value={batch.status} onChange={e => onStatusChange(e.target.value)}>
          {BATCH_STATUS.map(s => <option key={s} value={s}>{humanize(s)}</option>)}
        </select>
      </Field>

      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: dispositionColor }}>
          {disposition === 'order' ? `Linked to Order #${linkedOrderNumber || '?'}` : disposition === 'waste' ? 'Marked as waste' : 'Unlinked — assign below'}
        </div>
        <span className="spacer"/>
        {disposition !== 'order' && (
          <button className="btn btn-ghost btn-sm" onClick={() => setShowLinkOrder(v => !v)}>Link to order</button>
        )}
        {disposition === 'order' && (
          <button className="btn btn-danger btn-sm" onClick={async () => {
            await Production.unlinkFromOrder(batch.id);
            toast.push({ tone: 'ok', title: 'Batch unlinked' });
            onRefresh?.();
          }}>Unlink</button>
        )}
        {disposition !== 'waste' && (
          <button className="btn btn-danger btn-sm" onClick={() => setShowMarkWaste(v => !v)}>Mark as waste</button>
        )}
      </div>

      {showLinkOrder && <LinkOrderModal onSelect={handleLinkOrder} onClose={() => setShowLinkOrder(false)} />}
      {showMarkWaste && <MarkWasteModal onConfirm={handleMarkWaste} onClose={() => setShowMarkWaste(false)} />}

      {editingMeta ? (
        <div style={{ padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px dashed var(--border-strong)", marginBottom: 4 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 10 }}>
            <Field label="Date made">
              <input className="input" type="date" value={metaForm.date_made} onChange={e => setMetaForm(p => ({ ...p, date_made: e.target.value }))}/>
            </Field>
            <Field label="Flavors">
              {batchFlavorSets.length > 0 && (
                <select className="select" style={{ marginBottom: 4 }} value="" onChange={e => { if (e.target.value) setMetaForm(p => ({ ...p, flavors: e.target.value })); }}>
                  <option value="">— Pick flavor set —</option>
                  {batchFlavorSets.map(s => <option key={s.id} value={s.flavors}>{s.set_name}</option>)}
                </select>
              )}
              <input className="input" value={metaForm.flavors} onChange={e => setMetaForm(p => ({ ...p, flavors: e.target.value }))} placeholder="e.g. Ube, Keso, Mango"/>
            </Field>
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button className="btn btn-ghost btn-sm" onClick={() => setEditingMeta(false)}>Cancel</button>
            <button className="btn btn-primary btn-sm" onClick={saveMeta}>{I.check} Save</button>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 12, alignItems: "end", marginBottom: 4 }}>
          <Field label="Date made"><div>{formatDate(batch.date_made)}</div></Field>
          <Field label="Flavors"><div>{batch.flavors || <span className="faint">—</span>}</div></Field>
          <button className="btn btn-ghost btn-sm" onClick={() => setEditingMeta(true)}>{I.edit} Edit</button>
        </div>
      )}

      {batch.notes && <Field label="Notes"><div className="muted">{batch.notes}</div></Field>}

      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 12 }}>Ingredients</div>
        {ING_SECTIONS.map(s => <IngSection key={s.key} sectionKey={s.key} label={s.label} />)}
        {ingredients.filter(i => !i.ingredient_type).length > 0 && (
          <IngSection key="untagged" sectionKey={null} label="Other / Untagged" />
        )}
      </div>
      {confirmRemoveIngId && (
        <ConfirmModal title="Remove ingredient" message="Remove this ingredient from the batch?"
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveIngredient(confirmRemoveIngId)}
          onClose={() => setConfirmRemoveIngId(null)} />
      )}
      {confirmDeleteBatch && (
        <ConfirmModal title="Delete batch"
          message={`Delete this batch (${batch.product_name_snapshot} · ${formatDate(batch.date_made)})? It will also be unlinked from any order.`}
          confirmLabel="Delete" danger
          onConfirm={doDeleteBatch}
          onClose={() => setConfirmDeleteBatch(false)} />
      )}
    </Drawer>
  );
}

function LinkOrderModal({ onSelect, onClose }) {
  const [orders, setOrders] = React.useState(null);
  const [search, setSearch] = React.useState('');
  React.useEffect(() => {
    Orders.list().then(r => setOrders(Array.isArray(r) ? r : (r.data || [])));
  }, []);
  const filtered = (orders || []).filter(o =>
    !search || String(o.order_number).includes(search) || (o.contact_name || '').toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div style={{ marginBottom: 12, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px solid var(--border-strong)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13 }}>Link to Order</div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>{I.x}</button>
      </div>
      <input className="input" placeholder="Search order # or customer…" value={search} onChange={e => setSearch(e.target.value)} style={{ marginBottom: 8 }} />
      {orders === null ? <Spinner label="Loading orders…" /> : (
        <div style={{ maxHeight: 220, overflowY: "auto" }}>
          {filtered.slice(0, 30).map(o => (
            <div key={o.id}
              onClick={() => onSelect(o.id, o.order_number)}
              style={{ padding: "8px 10px", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}
              onMouseEnter={e => e.currentTarget.style.background = "var(--surface-3)"}
              onMouseLeave={e => e.currentTarget.style.background = ""}
            >
              <span className="mono" style={{ fontWeight: 700, fontSize: 13 }}>#{o.order_number}</span>
              <span style={{ fontSize: 12, color: "var(--text-muted)" }}>{o.contact_name || "—"}</span>
              <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--text-faint)" }}>{formatDate(o.delivery_date || o.created_at)}</span>
            </div>
          ))}
          {filtered.length === 0 && <div className="muted" style={{ fontSize: 12, textAlign: "center", padding: 12 }}>No orders found.</div>}
        </div>
      )}
    </div>
  );
}

function MarkWasteModal({ onConfirm, onClose }) {
  const [reason, setReason] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  async function confirm() {
    setBusy(true);
    await onConfirm(reason || null);
    setBusy(false);
  }
  return (
    <div style={{ marginBottom: 12, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px solid var(--danger)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: "var(--danger)" }}>Mark as Waste</div>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>{I.x}</button>
      </div>
      <Field label="Reason (optional)">
        <input className="input" placeholder="e.g. Quality reject, personal consumption…" value={reason} onChange={e => setReason(e.target.value)} />
      </Field>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-danger btn-sm" onClick={confirm} disabled={busy}>{busy ? "Saving…" : "Confirm waste"}</button>
      </div>
    </div>
  );
}

function NewBatchDrawer({ products, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState({ product_id: "", qty_produced: "1", unit: "tin (6x2)", flavors: "", date_made: new Date().toISOString().slice(0, 10), notes: "" });
  const [recipe, setRecipe] = React.useState([]);
  const [flavorSets, setFlavorSets] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  // Load recipe and flavor sets when product changes
  React.useEffect(() => {
    if (!f.product_id) { setRecipe([]); setFlavorSets([]); return; }
    Products.ingredients(f.product_id).then(r => setRecipe(Array.isArray(r) ? r : (r.data || [])));
    Products.flavorSets(f.product_id).then(r => setFlavorSets((Array.isArray(r) ? r : (r.data || [])).filter(s => s.active)));
  }, [f.product_id]);

  async function save() {
    if (!f.product_id || !f.qty_produced) return;
    setBusy(true);
    const qty = Math.max(1, Number(f.qty_produced) || 1);
    let created = 0;

    for (let i = 0; i < qty; i++) {
      const res = await Production.create({
        p_product_id: f.product_id,
        p_qty_produced: 1,
        p_unit: f.unit || "tin (6x2)",
        p_flavors: f.flavors || null,
        p_date_made: f.date_made,
        p_notes: f.notes || null,
      });
      if (res.error || !res.data) continue;
      created++;
      const batchId = res.data;
      if (recipe.length > 0) {
        await Promise.all(recipe.map(ing =>
          Production.addIngredient(batchId, ing.item_id, ing.qty_per_tin || 0, ing.unit, ing.notes || null, ing.qty_per_tin || null, ing.ingredient_type || null)
        ));
      }
    }

    setBusy(false);
    if (created > 0) {
      toast.push({ tone: created < qty ? 'warn' : 'ok', title: `${created} batch${created > 1 ? 'es' : ''} created`, msg: recipe.length > 0 ? `${recipe.length} ingredients per batch auto-filled from recipe` : "No recipe — add ingredients manually." });
      onSaved();
    } else {
      toast.push({ tone: 'danger', title: 'Failed to create batches' });
    }
  }

  return (
    <Drawer title="New batch" onClose={onClose}
      footer={<>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !f.product_id}>
          {busy ? "Saving…" : <>{I.check} Create</>}
        </button>
      </>}>
      <Field label="Product" required>
        <select className="select" value={f.product_id} onChange={e => set("product_id", e.target.value)}>
          <option value="">— Select product —</option>
          {products.filter(p => p.active !== false).map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      </Field>
      {recipe.length > 0 && (
        <div style={{ padding: "10px 12px", background: "var(--cyan-soft)", borderRadius: 10, fontSize: 12, marginBottom: 4, display: "flex", alignItems: "flex-start", gap: 8 }}>
          <span className="icon-sm" style={{ color: "var(--ok)", marginTop: 1, flexShrink: 0 }}>{I.check}</span>
          <span>Recipe found: {recipe.map(r => `${r.item_name} (${r.qty_per_tin} ${r.unit}/tin)`).join(", ")} — ingredients auto-filled per batch.</span>
        </div>
      )}
      <div className="field-grid">
        <Field label="How many tins?" required help="Creates one batch row per tin.">
          <input className="input" type="number" min="1" value={f.qty_produced} onChange={e => set("qty_produced", e.target.value)} />
        </Field>
        <Field label="Unit">
          <input className="input" value={f.unit} onChange={e => set("unit", e.target.value)} placeholder="tin (6x2)" />
        </Field>
      </div>
      {flavorSets.length > 0 && (
        <Field label="Flavor set" help="Select a preset to auto-fill the flavors field.">
          <select className="select" value="" onChange={e => { if (e.target.value) set("flavors", e.target.value); }}>
            <option value="">— None —</option>
            {flavorSets.map(s => <option key={s.id} value={s.flavors}>{s.set_name}</option>)}
          </select>
        </Field>
      )}
      <Field label="Flavors" help="Optional — describe the flavor profile.">
        <input className="input" value={f.flavors} onChange={e => set("flavors", e.target.value)} />
      </Field>
      <Field label="Date made">
        <input className="input" type="date" value={f.date_made} onChange={e => set("date_made", e.target.value)} />
      </Field>
      <Field label="Notes"><textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)} /></Field>
    </Drawer>
  );
}

/* ============================================================
 * INVENTORY VIEW
 * ============================================================ */

function InventoryView() {
  const toast = useToast();
  const [items, setItems] = React.useState(null);
  const [tab, setTab] = React.useState("all");
  const [filter, setFilter] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("");
  const [showUtensils, setShowUtensils] = React.useState(false);
  const [showUnused, setShowUnused] = React.useState(false);
  const [adjustFor, setAdjustFor] = React.useState(null);
  const [wasteFor, setWasteFor] = React.useState(null);
  const [editingItem, setEditingItem] = React.useState(null);
  const [adjustments, setAdjustments] = React.useState(null);
  const [editingAdj, setEditingAdj] = React.useState(null);

  React.useEffect(() => {
    const ctrl = new AbortController();
    Inventory.current(ctrl.signal)
      .then(r => setItems(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setItems([]));
    return () => ctrl.abort();
  }, []);

  React.useEffect(() => {
    if (tab === "adjustments" && adjustments === null) {
      Inventory.adjustments().then(r => setAdjustments(Array.isArray(r) ? r : (r.data || [])));
    }
  }, [tab]);

  const reloadAdjustments = () =>
    Inventory.adjustments().then(r => setAdjustments(Array.isArray(r) ? r : (r.data || [])));

  const itemNameById = React.useMemo(() => {
    const map = {};
    (items || []).forEach(i => { map[i.item_id] = i.item_name; });
    return map;
  }, [items]);

  const categories = React.useMemo(() => {
    if (!items) return [];
    return [...new Set(items.map(i => i.category_name).filter(Boolean))].sort();
  }, [items]);

  const lowBusiness = (items || []).filter(i =>
    (i.qty_remaining || 0) < 5 && i.category_id !== UTENSILS_CATEGORY_ID
  );

  const visible = (items || []).filter(i => {
    if (!showUtensils && i.category_id === UTENSILS_CATEGORY_ID) return false;
    // Hide items with no business activity (personal-only purchases) unless opted in
    if (!showUnused && (i.qty_purchased || 0) === 0 && (i.qty_consumed || 0) === 0 && (i.qty_adjusted || 0) === 0) return false;
    if (tab === "low" && (i.qty_remaining || 0) >= 5) return false;
    if (tab === "zero" && (i.qty_remaining || 0) !== 0) return false;
    if (catFilter && i.category_name !== catFilter) return false;
    if (filter && !(i.item_name || "").toLowerCase().includes(filter.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="view-enter">
      {lowBusiness.length > 0 && (
        <div className="card" style={{ marginBottom: 14, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, background: "linear-gradient(135deg, rgba(248,113,113,0.10), rgba(236,72,153,0.06))", border: "1px solid rgba(248,113,113,0.25)" }}>
          <span style={{ width: 38, height: 38, borderRadius: 12, background: "rgba(248,113,113,0.18)", color: "var(--danger)", display: "grid", placeItems: "center" }}>{I.warn}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700 }}>{lowBusiness.length} business items are below threshold</div>
            <div className="muted" style={{ fontSize: 12 }}>Utensils &amp; equipment excluded · from <span className="mono">inventory_current</span> view</div>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={() => setTab("low")}>View low stock</button>
        </div>
      )}

      <div className="filter-bar">
        <div className="seg">
          <button className={tab === "all" ? "active" : ""} onClick={() => setTab("all")}>All <span className="faint mono" style={{ marginLeft: 4 }}>{(items || []).length}</span></button>
          <button className={tab === "low" ? "active" : ""} onClick={() => setTab("low")}>Low stock <span className="faint mono" style={{ marginLeft: 4 }}>{lowBusiness.length}</span></button>
          <button className={tab === "zero" ? "active" : ""} onClick={() => setTab("zero")}>Zero</button>
          <button className={tab === "adjustments" ? "active" : ""} onClick={() => setTab("adjustments")}>Adjustments</button>
        </div>
        <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 240px", minWidth: 160 }}>
          <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
          <input placeholder="Search items…" value={filter} onChange={e => setFilter(e.target.value)} />
        </div>
        <select className="select" style={{ width: "auto", minWidth: 160 }}
                value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <label className="hide-sm" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          <input type="checkbox" checked={showUtensils} onChange={e => setShowUtensils(e.target.checked)} />
          Show Utensils &amp; Equipment
        </label>
        <label className="hide-sm" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--text-muted)" }}>
          <input type="checkbox" checked={showUnused} onChange={e => setShowUnused(e.target.checked)} />
          Show unused
        </label>
        <span className="spacer"/>
      </div>

      {tab === "adjustments"
        ? <div className="card">
            <div className="tbl-wrap">
              {adjustments === null
                ? <Spinner label="Loading adjustments…" />
                : adjustments.length === 0
                  ? <EmptyState title="No adjustments" hint="Waste and correction entries will appear here." />
                  : <table className="tbl">
                      <thead><tr>
                        <th>Date</th><th>Item</th><th>Type</th>
                        <th style={{ textAlign: "right" }}>Qty change</th>
                        <th>Unit</th><th>Notes</th><th></th>
                      </tr></thead>
                      <tbody>
                        {adjustments.map(a => (
                          <tr key={a.id}>
                            <td className="mono" style={{ fontSize: 12 }}>{formatDate(a.date)}</td>
                            <td style={{ fontWeight: 600 }}>{itemNameById[a.item_id] || <span className="faint">{a.item_id?.slice(0, 8)}</span>}</td>
                            <td><Pill>{humanize(a.adjustment_type || "—")}</Pill></td>
                            <td className="mono" style={{ textAlign: "right", color: a.qty_change < 0 ? "var(--danger)" : "var(--ok)" }}>{a.qty_change > 0 ? "+" : ""}{a.qty_change}</td>
                            <td className="mono faint" style={{ fontSize: 12 }}>{a.unit || "—"}</td>
                            <td className="muted" style={{ fontSize: 12 }}>{a.notes || "—"}</td>
                            <td style={{ textAlign: "right" }}>
                              <button className="btn btn-ghost btn-sm" onClick={() => setEditingAdj(a)}>{I.edit}</button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
              }
            </div>
          </div>
        : <div className="card">
            <div className="tbl-wrap">
              {items === null
                ? <Spinner label="Loading inventory…" />
                : visible.length === 0
                  ? <EmptyState title="Nothing to show" hint="Adjust your filters above." />
                  : <table className="tbl tbl-inventory">
                      <thead><tr>
                        <th>Item</th><th>Category</th>
                        <th style={{ textAlign: "right" }}>On hand</th>
                        <th>Unit</th>
                        <th style={{ textAlign: "right" }}>Avg cost</th>
                        <th style={{ textAlign: "right" }}>Value</th>
                        <th>Status</th>
                        <th></th>
                      </tr></thead>
                      <tbody>
                        {visible.map(i => {
                          const isLow = (i.qty_remaining || 0) < 5;
                          const isUtensil = i.category_id === UTENSILS_CATEGORY_ID;
                          const tone = isUtensil ? "muted" : isLow ? "danger" : "ok";
                          return (
                            <tr key={i.item_id} style={{ cursor: "default" }}>
                              <td style={{ fontWeight: 600 }}>{i.item_name}</td>
                              <td><Pill tone={isUtensil ? "muted" : "cyan"}>{i.category_name}</Pill></td>
                              <td className="amt" style={{ textAlign: "right" }}>{i.qty_remaining}</td>
                              <td className="mono faint" style={{ fontSize: 12 }}>{i.unit}</td>
                              <td className="amt" style={{ textAlign: "right" }}>{formatMoney(i.avg_unit_cost)}</td>
                              <td className="amt" style={{ textAlign: "right" }}>{formatMoney(i.value_remaining)}</td>
                              <td>
                                {isUtensil
                                  ? <Pill tone="muted">N/A</Pill>
                                  : isLow
                                    ? <Pill tone="danger"><span className="dot"/>Low</Pill>
                                    : <Pill tone="ok"><span className="dot"/>OK</Pill>}
                              </td>
                              <td style={{ textAlign: "right" }}>
                                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                                  <button className="btn btn-ghost btn-sm" title="Edit item" onClick={() => setEditingItem(i)}>{I.edit}</button>
                                  <button className="btn btn-ghost btn-sm" onClick={() => setWasteFor(i)}>Waste</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
              }
            </div>
          </div>
      }

      {wasteFor && (
        <WasteDrawer
          item={wasteFor}
          onClose={() => setWasteFor(null)}
          onSaved={() => {
            setWasteFor(null);
            toast.push({ tone: "ok", title: "Waste logged" });
            Inventory.current().then(r => setItems(Array.isArray(r) ? r : (r.data || [])));
            setAdjustments(null);
          }}
        />
      )}

      {editingAdj && (
        <AdjustmentEditDrawer
          row={editingAdj}
          onClose={() => setEditingAdj(null)}
          onSaved={() => { setEditingAdj(null); reloadAdjustments(); }}
        />
      )}

      {editingItem && (
        <ItemEditDrawer
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={() => {
            setEditingItem(null);
            toast.push({ tone: "ok", title: "Item updated" });
            Inventory.current().then(r => setItems(Array.isArray(r) ? r : (r.data || [])));
          }}
        />
      )}
    </div>
  );
}

const WASTE_TYPES = ["waste", "spoilage", "correction", "giveaway", "personal_use", "gift", "other"];

function WasteDrawer({ item, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState({
    qty: "",
    unit: item.unit || "",
    adjustment_type: "waste",
    date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  async function save() {
    if (!f.qty || Number(f.qty) <= 0) return;
    setBusy(true);
    const res = await rpc("log_waste", {
      p_item_id: item.item_id,
      p_qty: Number(f.qty),
      p_unit: f.unit,
      p_reason: f.notes || null,
      p_date: f.date,
    });
    setBusy(false);
    if (!res.error) onSaved();
    else toast.push({ tone: "danger", title: "Failed to log waste" });
  }

  return (
    <Drawer
      title={`Log waste · ${item.item_name}`}
      onClose={onClose}
      footer={<><span className="spacer"/><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={busy || !f.qty}>Save</button></>}
    >
      <Field label="Item"><div style={{ fontWeight: 600 }}>{item.item_name}</div></Field>
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Qty lost" required>
          <input className="input" type="number" step="0.001" min="0" value={f.qty} onChange={e => set("qty", e.target.value)} placeholder="0"/>
        </Field>
        <Field label="Unit">
          <input className="input" value={f.unit} onChange={e => set("unit", e.target.value)}/>
        </Field>
      </div>
      <Field label="Adjustment type">
        <select className="select" value={f.adjustment_type} onChange={e => set("adjustment_type", e.target.value)}>
          {WASTE_TYPES.map(v => <option key={v} value={v}>{humanize(v)}</option>)}
        </select>
      </Field>
      <Field label="Date">
        <input className="input" type="date" value={f.date} onChange={e => set("date", e.target.value)}/>
      </Field>
      <Field label="Notes">
        <textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)} placeholder="Optional reason or details"/>
      </Field>
    </Drawer>
  );
}

function ItemEditDrawer({ item, onClose, onSaved }) {
  const toast = useToast();
  const [cats, setCats] = React.useState(null);
  const [f, setF] = React.useState({ category_id: item.category_id || "", default_unit: item.unit || "" });
  const [busy, setBusy] = React.useState(false);

  React.useEffect(() => {
    Expenses.categories().then(r => setCats(Array.isArray(r) ? r : (r.data || [])));
  }, []);

  async function save() {
    setBusy(true);
    const res = await update("items", item.item_id, {
      category_id: f.category_id || null,
      default_unit: f.default_unit || null,
    });
    setBusy(false);
    if (!res.error) onSaved();
    else toast.push({ tone: "danger", title: "Failed to update item" });
  }

  return (
    <Drawer title={`Edit item: ${item.item_name}`} onClose={onClose}
      footer={<>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy || !f.default_unit}>{busy ? "Saving…" : "Save"}</button>
      </>}>
      <Field label="Item name">
        <div style={{ fontWeight: 600, padding: "8px 12px", background: "var(--surface-2)", borderRadius: 8, fontSize: 13, color: "var(--text-muted)" }}>{item.item_name}</div>
      </Field>
      <Field label="Category" help="Select from the permanent category list — no new categories are created here.">
        {cats === null
          ? <Spinner label="Loading…" />
          : <select className="select" value={f.category_id} onChange={e => setF(p => ({ ...p, category_id: e.target.value }))}>
              <option value="">— Uncategorized —</option>
              {cats.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>}
      </Field>
      <Field label="Default unit" help="Unit used in inventory tracking (e.g. pc, can, kg, ml, L, pack).">
        <input className="input" value={f.default_unit} onChange={e => setF(p => ({ ...p, default_unit: e.target.value }))} placeholder="pc" />
      </Field>
    </Drawer>
  );
}

const ADJ_TYPES = ["waste", "spoilage", "correction", "giveaway", "personal_use", "gift", "other", "opening_balance"];

function AdjustmentEditDrawer({ row, onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState({
    adjustment_type: row.adjustment_type || "correction",
    qty_change: row.qty_change ?? "",
    unit: row.unit || "",
    date: row.date || "",
    notes: row.notes || "",
  });
  const [busy, setBusy] = React.useState(false);
  const [confirmRestore, setConfirmRestore] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  async function save() {
    setBusy(true);
    const res = await update("inventory_adjustments", row.id, {
      adjustment_type: f.adjustment_type,
      qty_change: Number(f.qty_change),
      unit: f.unit || null,
      date: f.date,
      notes: f.notes || null,
    });
    setBusy(false);
    if (!res.error) { toast.push({ tone: "ok", title: "Adjustment updated" }); onSaved(); }
    else toast.push({ tone: "danger", title: "Failed to update" });
  }

  async function doRestore() {
    setBusy(true);
    const res = await update("inventory_adjustments", row.id, { qty_change: -Number(row.qty_change) });
    setBusy(false);
    if (!res.error) { toast.push({ tone: "ok", title: "Adjustment reversed" }); onSaved(); }
    else toast.push({ tone: "danger", title: "Failed to reverse" });
  }

  return (
    <Drawer
      title="Edit adjustment"
      onClose={onClose}
      footer={<>
        <button className="btn btn-ghost" onClick={() => setConfirmRestore(true)} disabled={busy}>Restore (reverse)</button>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>Save</button>
      </>}
    >
      <Field label="Adjustment type">
        <select className="select" value={f.adjustment_type} onChange={e => set("adjustment_type", e.target.value)}>
          {ADJ_TYPES.map(v => <option key={v} value={v}>{humanize(v)}</option>)}
        </select>
      </Field>
      <div className="grid c2" style={{ gap: 12 }}>
        <Field label="Qty change" help="Negative = reduction, positive = addition">
          <input className="input" type="number" step="0.001" value={f.qty_change} onChange={e => set("qty_change", e.target.value)}/>
        </Field>
        <Field label="Unit">
          <input className="input" value={f.unit} onChange={e => set("unit", e.target.value)}/>
        </Field>
      </div>
      <Field label="Date">
        <input className="input" type="date" value={f.date} onChange={e => set("date", e.target.value)}/>
      </Field>
      <Field label="Notes">
        <textarea className="textarea" value={f.notes} onChange={e => set("notes", e.target.value)} />
      </Field>
      {confirmRestore && (
        <ConfirmModal title="Reverse adjustment"
          message="This will set the qty change to the opposite value. Continue?"
          confirmLabel="Reverse"
          onConfirm={doRestore}
          onClose={() => setConfirmRestore(false)} />
      )}
    </Drawer>
  );
}

/* ============================================================
 * PRODUCTS VIEW
 * ============================================================ */
function ProductsView() {
  const [products, setProducts] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("");

  React.useEffect(() => {
    const ctrl = new AbortController();
    Products.list(ctrl.signal)
      .then(r => setProducts(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setProducts([]));
    return () => ctrl.abort();
  }, []);

  const refresh = () => Products.list().then(r => setProducts(Array.isArray(r) ? r : (r.data || [])));

  const filtered = (products || []).filter(p => {
    if (search && !(p.name || "").toLowerCase().includes(search.toLowerCase())) return false;
    if (catFilter && p.category !== catFilter) return false;
    if (activeFilter === "active" && p.active === false) return false;
    if (activeFilter === "inactive" && p.active !== false) return false;
    return true;
  });

  const categories = React.useMemo(() => [...new Set((products || []).map(p => p.category).filter(Boolean))], [products]);

  return (
    <div className="view-enter">
      <div className="filter-bar">
        <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 260px", minWidth: 180 }}>
          <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
          <input placeholder="Search products…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="select" style={{ width: "auto", minWidth: 160 }}
                value={catFilter} onChange={e => setCatFilter(e.target.value)}>
          <option value="">All categories</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <select className="select" style={{ width: "auto", minWidth: 130 }}
                value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
          <option value="">Active &amp; inactive</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
        <span className="spacer"/>
        <button className="btn btn-primary" onClick={() => setOpen("new")}>{I.plus} New product</button>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          {products === null
            ? <Spinner label="Loading products…" />
            : filtered.length === 0
              ? <EmptyState title="No products" hint={products.length ? "No products match your filters." : "Add your first product."} />
              : <table className="tbl">
                  <thead><tr>
                    <th>Name</th><th>Category</th>
                    <th style={{ textAlign: "right" }}>Retail</th>
                    <th style={{ textAlign: "right" }}>Reseller</th>
                    <th style={{ textAlign: "right" }}>Franchise</th>
                    <th>Status</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(p => (
                      <tr key={p.id} onClick={() => setOpen(p)}>
                        <td style={{ fontWeight: 600 }}>{p.name}</td>
                        <td>{p.category ? <Pill tone="muted">{p.category}</Pill> : <span className="faint">—</span>}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(p.price_retail)}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(p.price_reseller)}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(p.price_franchise)}</td>
                        <td><Pill tone={p.active !== false ? "ok" : "muted"}><span className="dot"/>{p.active !== false ? "Active" : "Inactive"}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
          }
        </div>
      </div>

      {open && <ProductDrawer
        product={open === "new" ? null : open}
        isNew={open === "new"}
        onClose={() => setOpen(null)}
        onSaved={() => { setOpen(null); refresh(); }}
      />}
    </div>
  );
}

function ProductDrawer({ product, isNew, onClose, onSaved }) {
  const toast = useToast();
  const empty = { name: "", category: "", active: true, sort_order: 0, image_url: "", notes: "", price_retail: "", price_reseller: "", price_franchise: "" };
  const [f, setF] = React.useState(product ? { ...empty, ...product, price_retail: product.price_retail ?? "", price_reseller: product.price_reseller ?? "", price_franchise: product.price_franchise ?? "" } : empty);
  const [busy, setBusy] = React.useState(false);
  const [ingredients, setIngredients] = React.useState([]);
  const [inventoryItems, setInventoryItems] = React.useState([]);
  const [showAddIng, setShowAddIng] = React.useState(false);
  const [addIngSection, setAddIngSection] = React.useState('base_flan');
  const [editingIng, setEditingIng] = React.useState(null);
  const [recipeIngredients, setRecipeIngredients] = React.useState(isNew ? [
    { item_id: '', item_name: 'Tin Can 6x2', qty_per_tin: 1, unit: 'tin (6x2)', notes: '', ingredient_type: 'packaging', tempId: 'default-tin' },
    { item_id: '', item_name: 'Brand Sticker', qty_per_tin: 1, unit: 'pc', notes: '', ingredient_type: 'packaging', tempId: 'default-sticker' },
  ] : []);
  const [newIngForm, setNewIngForm] = React.useState({ item_id: '', item_name: '', qty_per_tin: '', unit: '', notes: '', ingredient_type: 'base_flan' });
  const [confirmRemoveIngId, setConfirmRemoveIngId] = React.useState(null);
  const [confirmRemove, setConfirmRemove] = React.useState(false);
  const [flavorSets, setFlavorSets] = React.useState([]);
  const [editingSet, setEditingSet] = React.useState(null);
  const [showAddSet, setShowAddSet] = React.useState(false);
  const [newSetForm, setNewSetForm] = React.useState({ set_name: "", flavors: "", notes: "" });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  React.useEffect(() => {
    Production.items().then(r => {
      const its = Array.isArray(r) ? r : (r.data || []);
      setInventoryItems(its);
      if (isNew) {
        const tinId    = its.find(x => x.name === 'Tin Can 6x2')?.id   || '';
        const stickId  = its.find(x => x.name === 'Brand Sticker')?.id || '';
        setRecipeIngredients(prev => prev.map(ing => {
          if (ing.tempId === 'default-tin')     return { ...ing, item_id: tinId };
          if (ing.tempId === 'default-sticker') return { ...ing, item_id: stickId };
          return ing;
        }));
      }
    });
    if (!isNew && product?.id) {
      Products.ingredients(product.id).then(r => setIngredients(Array.isArray(r) ? r : (r.data || [])));
      Products.flavorSets(product.id).then(r => setFlavorSets(Array.isArray(r) ? r : (r.data || [])));
    }
  }, [isNew, product?.id]);

  const reloadFlavorSets = () => {
    if (!isNew && product?.id)
      Products.flavorSets(product.id).then(r => setFlavorSets(Array.isArray(r) ? r : (r.data || [])));
  };

  async function saveSet() {
    const res = await Products.updateFlavorSet(editingSet.id, {
      set_name: editingSet.set_name, flavors: editingSet.flavors,
      sort_order: editingSet.sort_order || 0, notes: editingSet.notes || null, active: editingSet.active,
    });
    if (!res.error) { setEditingSet(null); reloadFlavorSets(); toast.push({ tone: "ok", title: "Flavor set updated" }); }
  }

  async function addSet() {
    if (!newSetForm.set_name?.trim() || !newSetForm.flavors?.trim()) return;
    const res = await Products.addFlavorSet(product.id, newSetForm.set_name, newSetForm.flavors, 0, newSetForm.notes);
    if (!res.error) { setShowAddSet(false); setNewSetForm({ set_name: "", flavors: "", notes: "" }); reloadFlavorSets(); toast.push({ tone: "ok", title: "Flavor set added" }); }
  }

  async function removeSet(id) {
    await Products.removeFlavorSet(id);
    reloadFlavorSets();
    toast.push({ tone: "ok", title: "Flavor set removed" });
  }

  const reloadIngredients = () => {
    if (!isNew && product?.id) {
      Products.ingredients(product.id).then(r => setIngredients(Array.isArray(r) ? r : (r.data || [])));
    }
  };

  async function saveEditIng() {
    if (!editingIng) return;
    const res = await Products.upsertIngredient(product.id, editingIng.item_id, Number(editingIng.qty_per_tin), editingIng.unit, editingIng.notes || null, editingIng.ingredient_type || 'base_flan');
    if (!res.error) {
      setEditingIng(null);
      toast.push({ tone: 'ok', title: 'Ingredient updated' });
      reloadIngredients();
    }
  }

  async function doRemoveIngredient(id) {
    await Products.removeIngredient(id);
    toast.push({ tone: "ok", title: "Ingredient removed" });
    reloadIngredients();
  }

  async function save() {
    if (!f.name?.trim()) return;
    setBusy(true);
    const payload = {
      name: f.name, category: f.category || null, active: f.active !== false,
      sort_order: Number(f.sort_order) || 0, image_url: f.image_url || null, notes: f.notes || null,
      price_retail: f.price_retail !== "" ? Number(f.price_retail) : null,
      price_reseller: f.price_reseller !== "" ? Number(f.price_reseller) : null,
      price_franchise: f.price_franchise !== "" ? Number(f.price_franchise) : null,
    };
    if (isNew) {
      const res = await Products.create(payload);
      setBusy(false);
      if (!res.error) {
        if (recipeIngredients.length > 0) {
          const results = await Promise.allSettled(
            recipeIngredients.map(ing => Products.upsertIngredient(res.data, ing.item_id, ing.qty_per_tin, ing.unit, ing.notes || null, ing.ingredient_type || 'base_flan'))
          );
          const failed = results.filter(r => r.status === 'rejected' || r.value?.error).length;
          toast.push({ tone: failed ? 'warn' : 'ok', title: 'Product created', msg: failed ? `${failed} ingredient(s) failed to save.` : `${recipeIngredients.length} ingredient(s) added.` });
        } else {
          toast.push({ tone: 'ok', title: 'Product created' });
        }
        onSaved();
      }
    } else {
      const res = await Products.update(product.id, payload);
      setBusy(false);
      if (!res.error) {
        toast.push({ tone: "ok", title: "Product updated" });
        onSaved();
      }
    }
  }

  async function doRemove() {
    setBusy(true);
    const res = await Products.remove(product.id);
    setBusy(false);
    if (!res.error) {
      toast.push({ tone: "ok", title: "Product archived" });
      onSaved();
    }
  }

  return (
    <Drawer
      title={isNew ? "New product" : `Edit · ${product.name}`}
      onClose={onClose}
      footer={<>
        {!isNew && <button className="btn btn-danger" onClick={() => setConfirmRemove(true)} disabled={busy}>{I.trash}</button>}
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : <>{I.check} Save</>}</button>
      </>}
    >
      <Field label="Name" required>
        <input className="input" value={f.name} onChange={e => set("name", e.target.value)} />
      </Field>
      <Field label="Category">
        <EnumSelect value={f.category} onChange={v => set("category", v)} options={ENUM_OPTIONS.product_category} allowEmpty placeholder="— Select category —" />
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <Field label="Retail price"><input className="input" type="number" step="0.01" value={f.price_retail} onChange={e => set("price_retail", e.target.value)} /></Field>
        <Field label="Reseller price"><input className="input" type="number" step="0.01" value={f.price_reseller} onChange={e => set("price_reseller", e.target.value)} /></Field>
        <Field label="Franchise price"><input className="input" type="number" step="0.01" value={f.price_franchise} onChange={e => set("price_franchise", e.target.value)} /></Field>
      </div>
      <Field label="Sort order">
        <input className="input" type="number" value={f.sort_order} onChange={e => set("sort_order", e.target.value)} />
      </Field>
      <Field label="Image URL" help="Optional — public URL to product image.">
        <input className="input" type="url" value={f.image_url || ""} onChange={e => set("image_url", e.target.value)} />
      </Field>
      <Field label="Active">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={f.active !== false} onChange={e => set("active", e.target.checked)} />
          Product is visible and orderable
        </label>
      </Field>
      <Field label="Notes"><textarea className="textarea" value={f.notes || ""} onChange={e => set("notes", e.target.value)} /></Field>

      <div style={{ marginTop: 20 }}>
        <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-faint)", fontWeight: 600, marginBottom: 12 }}>Recipe / Ingredients per tin</div>

        {ING_SECTIONS.map(({ key: sKey, label: sLabel }) => {
          const sRows = isNew
            ? recipeIngredients.filter(r => r.ingredient_type === sKey)
            : ingredients.filter(i => i.ingredient_type === sKey && i.active !== false);
          const isAddingHere = showAddIng && addIngSection === sKey;

          return (
            <div key={sKey} style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-faint)", fontWeight: 600 }}>{sLabel}</div>
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  if (isAddingHere) { setShowAddIng(false); }
                  else { setAddIngSection(sKey); setNewIngForm({ item_id: '', item_name: '', qty_per_tin: '', unit: '', notes: '', ingredient_type: sKey }); setShowAddIng(true); }
                }}>{isAddingHere ? I.x : <>{I.plus} Add</>}</button>
              </div>

              {sRows.length > 0 && (
                <table className="tbl">
                  <thead><tr><th>Item</th><th style={{ textAlign: "right" }}>Qty/tin</th><th>Unit</th><th></th></tr></thead>
                  <tbody>
                    {isNew
                      ? sRows.map(ing => (
                          <tr key={ing.tempId} style={{ cursor: "default" }}>
                            <td style={{ fontWeight: 600 }}>{ing.item_name}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{ing.qty_per_tin}</td>
                            <td className="muted">{ing.unit}</td>
                            <td style={{ textAlign: "right" }}>
                              <button className="btn btn-danger btn-sm" onClick={() => setRecipeIngredients(p => p.filter(x => x.tempId !== ing.tempId))}>{I.trash}</button>
                            </td>
                          </tr>
                        ))
                      : sRows.map(i => {
                          const isEditing = editingIng?.id === i.id;
                          if (isEditing) return (
                            <tr key={i.id}>
                              <td style={{ fontWeight: 600 }}>{i.item_name}</td>
                              <td><input className="input" type="number" step="0.001" value={editingIng.qty_per_tin} style={{ width: 80 }} onChange={e => setEditingIng({ ...editingIng, qty_per_tin: e.target.value })} /></td>
                              <td><input className="input" value={editingIng.unit} style={{ width: 70 }} onChange={e => setEditingIng({ ...editingIng, unit: e.target.value })} /></td>
                              <td style={{ textAlign: 'right' }}>
                                <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                                  <button className="btn btn-primary btn-sm" onClick={saveEditIng}>{I.check}</button>
                                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingIng(null)}>{I.x}</button>
                                </div>
                              </td>
                            </tr>
                          );
                          return (
                            <tr key={i.id} style={{ cursor: "default" }}>
                              <td style={{ fontWeight: 600 }}>{i.item_name}</td>
                              <td className="mono" style={{ textAlign: "right" }}>{i.qty_per_tin}</td>
                              <td className="muted">{i.unit}</td>
                              <td style={{ textAlign: "right" }}>
                                <div style={{ display: "flex", gap: 4, justifyContent: "flex-end" }}>
                                  <button className="btn btn-ghost btn-sm" onClick={() => setEditingIng({ id: i.id, item_id: i.item_id, qty_per_tin: String(i.qty_per_tin), unit: i.unit || '', notes: i.notes || '', ingredient_type: i.ingredient_type || 'base_flan' })}>{I.edit}</button>
                                  <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemoveIngId(i.id)}>{I.trash}</button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                    }
                  </tbody>
                </table>
              )}

              {isAddingHere && (
                <div style={{ marginTop: 8, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px dashed var(--border-strong)" }}>
                  <Field label="Item">
                    <select className="select" value={newIngForm.item_id} onChange={e => {
                      const it = inventoryItems.find(x => x.id === e.target.value);
                      setNewIngForm(p => ({ ...p, item_id: e.target.value, item_name: it?.name || '', unit: it?.default_unit || p.unit }));
                    }}>
                      <option value="">— Select item —</option>
                      {inventoryItems.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
                    </select>
                  </Field>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                    <Field label="Qty per tin"><input className="input" type="number" step="0.001" value={newIngForm.qty_per_tin} onChange={e => setNewIngForm(p => ({ ...p, qty_per_tin: e.target.value }))} /></Field>
                    <Field label="Unit"><input className="input" value={newIngForm.unit} onChange={e => setNewIngForm(p => ({ ...p, unit: e.target.value }))} /></Field>
                  </div>
                  <Field label="Notes"><input className="input" value={newIngForm.notes} onChange={e => setNewIngForm(p => ({ ...p, notes: e.target.value }))} /></Field>
                  <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                    <button className="btn btn-ghost btn-sm" onClick={() => setShowAddIng(false)}>Cancel</button>
                    {isNew
                      ? <button className="btn btn-primary btn-sm" onClick={() => {
                          if (!newIngForm.item_id || !newIngForm.qty_per_tin) return;
                          setRecipeIngredients(p => [...p, { ...newIngForm, qty_per_tin: Number(newIngForm.qty_per_tin), tempId: Date.now() + Math.random() }]);
                          setNewIngForm({ item_id: '', item_name: '', qty_per_tin: '', unit: '', notes: '', ingredient_type: sKey });
                          setShowAddIng(false);
                        }} disabled={!newIngForm.item_id || !newIngForm.qty_per_tin}>{I.plus} Add</button>
                      : <button className="btn btn-primary btn-sm" onClick={async () => {
                          if (!newIngForm.item_id || !newIngForm.qty_per_tin) return;
                          const res = await Products.upsertIngredient(product.id, newIngForm.item_id, Number(newIngForm.qty_per_tin), newIngForm.unit, newIngForm.notes || null, sKey);
                          if (!res.error) {
                            setNewIngForm({ item_id: '', item_name: '', qty_per_tin: '', unit: '', notes: '', ingredient_type: 'base_flan' });
                            setShowAddIng(false);
                            toast.push({ tone: "ok", title: "Ingredient added" });
                            reloadIngredients();
                          }
                        }} disabled={!newIngForm.item_id || !newIngForm.qty_per_tin}>{I.plus} Add</button>
                    }
                  </div>
                </div>
              )}

              {sRows.length === 0 && !isAddingHere && (
                <div style={{ fontSize: 12, color: "var(--text-faint)", padding: "4px 0 2px" }}>No {sLabel.toLowerCase()} added.</div>
              )}
            </div>
          );
        })}
      </div>

      {!isNew && (
        <div style={{ marginTop: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "var(--text-faint)", fontWeight: 600 }}>Flavor sets</div>
            <button className="btn btn-ghost btn-sm" onClick={() => setShowAddSet(v => !v)}>{I.plus} Add set</button>
          </div>
          {flavorSets.filter(s => s.active).length === 0 && !showAddSet
            ? <EmptyState title="No flavor sets" hint="Add named flavor combinations for this product." />
            : <table className="tbl">
                <thead><tr><th>Set name</th><th>Flavors</th><th>Notes</th><th></th></tr></thead>
                <tbody>
                  {flavorSets.filter(s => s.active).map(s => {
                    const isEditing = editingSet?.id === s.id;
                    if (isEditing) return (
                      <tr key={s.id} style={{ background: "var(--surface-2)" }}>
                        <td><input className="input" style={{ minWidth: 100 }} value={editingSet.set_name} onChange={e => setEditingSet(p => ({ ...p, set_name: e.target.value }))}/></td>
                        <td><input className="input" style={{ minWidth: 160 }} value={editingSet.flavors} onChange={e => setEditingSet(p => ({ ...p, flavors: e.target.value }))}/></td>
                        <td><input className="input" value={editingSet.notes || ""} onChange={e => setEditingSet(p => ({ ...p, notes: e.target.value }))}/></td>
                        <td style={{ textAlign: "right" }}>
                          <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                            <button className="btn btn-ghost btn-sm" onClick={saveSet}>{I.check}</button>
                            <button className="btn btn-ghost btn-sm" onClick={() => setEditingSet(null)}>{I.x}</button>
                          </div>
                        </td>
                      </tr>
                    );
                    return (
                      <tr key={s.id}>
                        <td style={{ fontWeight: 600 }}>{s.set_name}</td>
                        <td className="muted" style={{ fontSize: 12 }}>{s.flavors}</td>
                        <td className="muted" style={{ fontSize: 12 }}>{s.notes || "—"}</td>
                        <td style={{ textAlign: "right" }}>
                          <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                            <button className="btn btn-ghost btn-sm" onClick={() => setEditingSet({ ...s })}>{I.edit}</button>
                            <button className="btn btn-danger btn-sm" onClick={() => removeSet(s.id)}>{I.trash}</button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          }
          {showAddSet && (
            <div style={{ marginTop: 12, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px dashed var(--border-strong)" }}>
              <div style={{ fontSize: 12, fontWeight: 700, marginBottom: 10 }}>New flavor set</div>
              <div className="grid c2" style={{ gap: 8, marginBottom: 8 }}>
                <Field label="Set name" required><input className="input" value={newSetForm.set_name} onChange={e => setNewSetForm(p => ({ ...p, set_name: e.target.value }))} placeholder="e.g. Set A"/></Field>
                <Field label="Flavors" required><input className="input" value={newSetForm.flavors} onChange={e => setNewSetForm(p => ({ ...p, flavors: e.target.value }))} placeholder="e.g. Ube, Keso, Mango"/></Field>
              </div>
              <Field label="Notes"><input className="input" value={newSetForm.notes} onChange={e => setNewSetForm(p => ({ ...p, notes: e.target.value }))}/></Field>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                <button className="btn btn-ghost btn-sm" onClick={() => setShowAddSet(false)}>Cancel</button>
                <button className="btn btn-primary btn-sm" onClick={addSet} disabled={!newSetForm.set_name || !newSetForm.flavors}>{I.plus} Add</button>
              </div>
            </div>
          )}
        </div>
      )}
      {confirmRemoveIngId && (
        <ConfirmModal title="Remove ingredient" message="Remove this ingredient from the recipe?"
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveIngredient(confirmRemoveIngId)}
          onClose={() => setConfirmRemoveIngId(null)} />
      )}
      {confirmRemove && (
        <ConfirmModal title="Archive product" message={`Archive product "${product?.name}"?`}
          confirmLabel="Archive" danger
          onConfirm={doRemove}
          onClose={() => setConfirmRemove(false)} />
      )}
    </Drawer>
  );
}


/* ============================================================
 * EXPENSES VIEW
 * ============================================================ */
function ExpensesView() {
  const [receipts, setReceipts] = React.useState(null);
  const [categories, setCategories] = React.useState([]);
  const [open, setOpen] = React.useState(null);
  const [search, setSearch] = React.useState("");
  const [catFilter, setCatFilter] = React.useState("");
  const [from, setFrom] = React.useState("");
  const [to, setTo] = React.useState("");

  React.useEffect(() => {
    const ctrl = new AbortController();
    Promise.all([
      Expenses.receipts(ctrl.signal),
      Expenses.categories(ctrl.signal),
    ]).then(([r, c]) => {
      if (ctrl.signal.aborted) return;
      setReceipts(Array.isArray(r) ? r : (r.data || []));
      setCategories(Array.isArray(c) ? c : (c.data || []));
    }).catch(() => setReceipts([]));
    return () => ctrl.abort();
  }, []);

  const refresh = () => {
    Expenses.receipts().then(r => setReceipts(Array.isArray(r) ? r : (r.data || [])));
  };

  const filtered = (receipts || []).filter(r => {
    if (search) {
      const s = search.toLowerCase();
      if (!(r.vendor_name || "").toLowerCase().includes(s) && !(r.or_invoice_number || "").includes(s)) return false;
    }
    if (from && (r.date || "").slice(0, 10) < from) return false;
    if (to && (r.date || "").slice(0, 10) > to) return false;
    return true;
  });

  return (
    <div className="view-enter">
      <div className="filter-bar">
        <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 260px", minWidth: 180 }}>
          <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
          <input placeholder="Search vendor or OR #…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="date-range-filter">
          <input className="input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
          <span className="date-range-sep">–</span>
          <input className="input" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
        <span className="spacer"/>
      </div>

      <div className="card">
        <div className="tbl-wrap">
          {receipts === null
            ? <Spinner label="Loading expenses…" />
            : filtered.length === 0
              ? <EmptyState title="No receipts" hint={receipts.length ? "No receipts match your filters." : "No expense receipts yet."} />
              : <table className="tbl">
                  <thead><tr>
                    <th>Date</th><th>Vendor</th><th>OR / Invoice #</th>
                    <th>Payment</th>
                    <th style={{ textAlign: "right" }}>Total</th>
                    <th>BIR Valid</th>
                  </tr></thead>
                  <tbody>
                    {filtered.map(r => (
                      <tr key={r.id} onClick={() => setOpen(r)} className={open?.id === r.id ? "selected" : ""}>
                        <td className="muted">{formatDate(r.date)}</td>
                        <td style={{ fontWeight: 600 }}>{r.vendor_name || <span className="faint">—</span>}</td>
                        <td className="mono" style={{ fontSize: 12 }}>{r.or_invoice_number || <span className="faint">—</span>}</td>
                        <td>{r.payment_method ? <Pill tone="muted">{humanize(r.payment_method)}</Pill> : <span className="faint">—</span>}</td>
                        <td className="amt" style={{ textAlign: "right" }}>{formatMoney(r.total)}</td>
                        <td><Pill tone={r.bir_valid ? "ok" : "warn"}>{r.bir_valid ? "Valid" : "No"}</Pill></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
          }
        </div>
      </div>

      {open && <ReceiptDrawer receipt={open} categories={categories} onClose={() => setOpen(null)} onChanged={refresh} />}
    </div>
  );
}

function ReceiptDrawer({ receipt, categories, onClose, onChanged }) {
  const toast = useToast();
  const [lines, setLines] = React.useState([]);
  const [showAdd, setShowAdd] = React.useState(false);
  const [editingLine, setEditingLine] = React.useState(null);
  const [editedFields, setEditedFields] = React.useState({});
  const [confirmRemoveLineId, setConfirmRemoveLineId] = React.useState(null);
  const [confirmRemoveReceipt, setConfirmRemoveReceipt] = React.useState(false);

  React.useEffect(() => {
    Expenses.lines(receipt.id)
      .then(r => setLines(Array.isArray(r) ? r : (r.data || [])));
  }, [receipt.id]);

  const reloadLines = () => Expenses.lines(receipt.id).then(r => setLines(Array.isArray(r) ? r : (r.data || [])));

  function startEdit(l) {
    setEditingLine(l);
    setEditedFields({
      item_name_snapshot: l.item_name_snapshot,
      quantity: l.quantity,
      unit: l.unit || "",
      unit_cost: l.unit_cost,
      category_id: l.category_id || "",
      business_or_personal: l.business_or_personal || "business",
    });
  }

  async function saveLine() {
    const res = await Expenses.updateLine({ id: editingLine.id, ...editedFields });
    if (!res.error) {
      setEditingLine(null);
      toast.push({ tone: "ok", title: "Line updated" });
      await reloadLines(); onChanged();
    }
  }

  async function doRemoveLine(id) {
    await Expenses.removeLine(id);
    toast.push({ tone: "ok", title: "Line removed" });
    await reloadLines(); onChanged();
  }

  async function doRemoveReceipt() {
    await Expenses.removeReceipt(receipt.id);
    toast.push({ tone: "ok", title: "Receipt deleted" });
    onClose(); onChanged();
  }

  return (
    <Drawer
      title={<><span className="txt-grad">{receipt.vendor_name || "Receipt"}</span></>}
      subtitle={`${formatDate(receipt.date)} · ${receipt.or_invoice_number || "no OR #"} · ${formatMoney(receipt.total)}`}
      onClose={onClose}
      footer={<>
        <button className="btn btn-danger" onClick={() => setConfirmRemoveReceipt(true)}>{I.trash} Delete receipt</button>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Close</button>
      </>}
    >
      <div style={{ marginBottom: 14 }}>
        <div className="row between" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Line items</div>
          <button className="btn btn-ghost btn-sm" onClick={() => setShowAdd(true)}>{I.plus} Add line</button>
        </div>
        {lines.length === 0
          ? <EmptyState title="No line items" hint="Add individual expense line items." />
          : <table className="tbl">
              <thead><tr>
                <th>Item</th><th>Category</th>
                <th style={{ textAlign: "right" }}>Qty</th><th>Unit</th>
                <th style={{ textAlign: "right" }}>Unit cost</th>
                <th style={{ textAlign: "right" }}>Subtotal</th>
                <th>Class</th><th></th>
              </tr></thead>
              <tbody>
                {lines.map(l => {
                  const isEditing = editingLine?.id === l.id;
                  const ef = editedFields;
                  const setEf = (k, v) => setEditedFields(p => ({ ...p, [k]: v }));
                  if (isEditing) return (
                    <tr key={l.id} style={{ background: "var(--surface-2)" }}>
                      <td><input className="input" style={{ minWidth: 120 }} value={ef.item_name_snapshot} onChange={e => setEf("item_name_snapshot", e.target.value)}/></td>
                      <td>
                        <select className="select" value={ef.category_id} onChange={e => setEf("category_id", e.target.value)}>
                          <option value="">— None —</option>
                          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      </td>
                      <td><input className="input mono" type="number" step="0.01" style={{ width: 70, textAlign: "right" }} value={ef.quantity} onChange={e => setEf("quantity", e.target.value)}/></td>
                      <td><input className="input mono" style={{ width: 60, fontSize: 12 }} value={ef.unit} onChange={e => setEf("unit", e.target.value)}/></td>
                      <td><input className="input mono" type="number" step="0.01" style={{ width: 90, textAlign: "right" }} value={ef.unit_cost} onChange={e => setEf("unit_cost", e.target.value)}/></td>
                      <td className="amt" style={{ textAlign: "right" }}>—</td>
                      <td>
                        <select className="select" value={ef.business_or_personal} onChange={e => setEf("business_or_personal", e.target.value)}>
                          <option value="business">Business</option>
                          <option value="personal">Personal</option>
                        </select>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                          <button className="btn btn-ghost btn-sm" onClick={saveLine} title="Save">{I.check}</button>
                          <button className="btn btn-ghost btn-sm" onClick={() => setEditingLine(null)} title="Cancel">{I.x}</button>
                        </div>
                      </td>
                    </tr>
                  );
                  return (
                    <tr key={l.id} style={{ cursor: "default" }}>
                      <td style={{ fontWeight: 600 }}>{l.item_name_snapshot}</td>
                      <td className="muted" style={{ fontSize: 12 }}>{l.category_name || "—"}</td>
                      <td className="mono" style={{ textAlign: "right" }}>{l.quantity}</td>
                      <td className="mono faint" style={{ fontSize: 12 }}>{l.unit}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{formatMoney(l.unit_cost)}</td>
                      <td className="amt" style={{ textAlign: "right" }}>{formatMoney(l.subtotal)}</td>
                      <td><Pill tone={l.business_or_personal === "business" ? "cyan" : "muted"}>{humanize(l.business_or_personal || "—")}</Pill></td>
                      <td style={{ textAlign: "right" }}>
                        <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEdit(l)}>{I.edit}</button>
                          <button className="btn btn-danger btn-sm" onClick={() => setConfirmRemoveLineId(l.id)}>{I.trash}</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
        }
      </div>
      {showAdd && <ExpenseLineForm receipt={receipt} categories={categories} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); reloadLines(); onChanged(); }} />}
      {confirmRemoveLineId && (
        <ConfirmModal title="Remove line item" message="Remove this line item from the receipt?"
          confirmLabel="Remove" danger
          onConfirm={() => doRemoveLine(confirmRemoveLineId)}
          onClose={() => setConfirmRemoveLineId(null)} />
      )}
      {confirmRemoveReceipt && (
        <ConfirmModal title="Delete receipt" message="Delete this entire receipt and all its line items?"
          confirmLabel="Delete" danger
          onConfirm={doRemoveReceipt}
          onClose={() => setConfirmRemoveReceipt(false)} />
      )}
    </Drawer>
  );
}

function ExpenseLineForm({ receipt, categories, onClose, onSaved }) {
  const toast = useToast();
  const [items, setItems] = React.useState([]);
  const [f, setF] = React.useState({ item_name_snapshot: "", item_ref_id: "", category_id: "", quantity: 1, unit: "", unit_cost: "", business_or_personal: "business", notes: "" });
  const [busy, setBusy] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  React.useEffect(() => {
    Expenses.itemsLookup().then(r => setItems(Array.isArray(r) ? r : (r.data || [])));
  }, []);

  function pickItem(id) {
    const it = items.find(x => x.id === id);
    if (it) set("item_name_snapshot", it.name);
    set("item_ref_id", id);
    if (it?.default_unit && !f.unit) set("unit", it.default_unit);
  }

  async function save() {
    if (!f.item_name_snapshot?.trim()) return;
    setBusy(true);
    const res = await Expenses.addLine({
      p_receipt_id: receipt.id,
      p_item_id: f.item_ref_id || null,
      p_item_name_snapshot: f.item_name_snapshot,
      p_category_id: f.category_id || null,
      p_quantity: Number(f.quantity) || 1,
      p_unit: f.unit || null,
      p_unit_cost: Number(f.unit_cost) || 0,
      p_business_or_personal: f.business_or_personal || "business",
      p_notes: f.notes || null,
    });
    setBusy(false);
    if (!res.error) {
      toast.push({ tone: "ok", title: "Line item added" });
      onSaved();
    }
  }

  return (
    <div style={{ marginTop: 16, padding: 14, background: "var(--surface-2)", borderRadius: 12, border: "1px dashed var(--border-strong)" }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-faint)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Add line item</div>
      <Field label="Inventory item (optional)">
        <select className="select" value={f.item_ref_id} onChange={e => pickItem(e.target.value)}>
          <option value="">— None (type name manually) —</option>
          {items.map(i => <option key={i.id} value={i.id}>{i.name}</option>)}
        </select>
      </Field>
      <Field label="Item name" required>
        <input className="input" value={f.item_name_snapshot} onChange={e => set("item_name_snapshot", e.target.value)} placeholder="Item description" />
      </Field>
      <Field label="Category">
        <select className="select" value={f.category_id} onChange={e => set("category_id", e.target.value)}>
          <option value="">— Select category —</option>
          {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
        <Field label="Qty"><input className="input" type="number" step="0.01" value={f.quantity} onChange={e => set("quantity", e.target.value)} /></Field>
        <Field label="Unit"><input className="input" value={f.unit} onChange={e => set("unit", e.target.value)} /></Field>
        <Field label="Unit cost"><input className="input" type="number" step="0.01" value={f.unit_cost} onChange={e => set("unit_cost", e.target.value)} /></Field>
        <Field label="Classification">
          <select className="select" value={f.business_or_personal} onChange={e => set("business_or_personal", e.target.value)}>
            <option value="business">Business</option>
            <option value="personal">Personal</option>
          </select>
        </Field>
      </div>
      <Field label="Notes"><input className="input" value={f.notes} onChange={e => set("notes", e.target.value)} /></Field>
      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
        <button className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary btn-sm" onClick={save} disabled={busy}>{I.plus} Add</button>
      </div>
    </div>
  );
}

/* ============================================================
 * USERS VIEW (admin-only)
 * ============================================================ */
function UsersView() {
  const { profile } = useAuth();
  const currentRole = profile ? profile.role : "viewer";

  const [tab, setTab] = React.useState("users");
  const [rows, setRows] = React.useState(null);
  const [open, setOpen] = React.useState(null);
  const [inviting, setInviting] = React.useState(false);
  const [exporting, setExporting] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [roleFilter, setRoleFilter] = React.useState("");
  const [activeFilter, setActiveFilter] = React.useState("");

  React.useEffect(() => {
    const ctrl = new AbortController();
    Users.list(ctrl.signal)
      .then(r => setRows(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setRows([]));
    return () => ctrl.abort();
  }, []);

  const refresh = () => Users.list().then(r => setRows(Array.isArray(r) ? r : (r.data || [])));

  async function runExport() {
    setExporting(true);
    try {
      const [orders, orderItems, batches, ingredients, receipts, lines, inventory, contacts] = await Promise.all([
        Orders.list(),
        Orders.allItems(),
        Production.batches(),
        Production.allIngredients(),
        Expenses.receipts(),
        Expenses.allLines(),
        Inventory.current(),
        Contacts.list(),
      ]);
      const bundle = {
        exported_at: new Date().toISOString(),
        orders:                  orders.data || [],
        order_items:             orderItems.data || [],
        batches:                 batches.data || [],
        production_ingredients:  ingredients.data || [],
        receipts:                receipts.data || [],
        line_items:              lines.data || [],
        inventory:               inventory.data || [],
        contacts:                contacts.data || [],
      };
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wyf-backup-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } finally {
      setExporting(false);
    }
  }

  if (currentRole !== "admin") {
    return (
      <div className="view-enter">
        <EmptyState title="Admins only" hint="You need admin access to manage users." />
      </div>
    );
  }

  const filtered = (rows || []).filter(r => {
    if (roleFilter && r.role !== roleFilter) return false;
    if (activeFilter === "active" && !r.active) return false;
    if (activeFilter === "inactive" && r.active) return false;
    if (search) {
      const s = search.toLowerCase();
      return (r.full_name || "").toLowerCase().includes(s) || (r.email || "").toLowerCase().includes(s);
    }
    return true;
  });

  return (
    <div className="view-enter">
      <div className="seg" style={{ marginBottom: 12 }}>
        <button className={"btn btn-sm" + (tab === "users" ? " active" : "")} onClick={() => setTab("users")}>Users</button>
        <button className={"btn btn-sm" + (tab === "logins" ? " active" : "")} onClick={() => setTab("logins")}>Login History</button>
        <button className={"btn btn-sm" + (tab === "audit" ? " active" : "")} onClick={() => setTab("audit")}>Audit Log</button>
      </div>

      {tab === "users" && <>
        <div className="filter-bar">
          <div className="searchbar" style={{ marginBottom: 0, flex: "0 1 260px", minWidth: 180 }}>
            <span style={{ width: 16, height: 16, display: "grid", placeItems: "center" }}>{I.search}</span>
            <input placeholder="Search name or email…" value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <select className="select" style={{ width: "auto", minWidth: 140 }} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
            <option value="">All roles</option>
            {ENUM_OPTIONS.user_role.map(r => <option key={r} value={r}>{humanize(r)}</option>)}
          </select>
          <select className="select" style={{ width: "auto", minWidth: 130 }} value={activeFilter} onChange={e => setActiveFilter(e.target.value)}>
            <option value="">Active &amp; inactive</option>
            <option value="active">Active only</option>
            <option value="inactive">Inactive only</option>
          </select>
          <button className="btn btn-primary" onClick={() => setInviting(true)}>{I.plus} Invite user</button>
          <button className="btn btn-ghost" onClick={runExport} disabled={exporting} title="Export all data as JSON backup">
            {exporting ? "Exporting…" : "Export data"}
          </button>
        </div>

        <div className="card">
          <div className="tbl-wrap">
            {rows === null
              ? <Spinner label="Loading users…" />
              : filtered.length === 0
                ? <EmptyState title="No users" hint="No users match your filters." />
                : <table className="tbl">
                    <thead><tr>
                      <th>Name</th><th>Email</th><th>Role</th><th>Status</th><th>Created</th>
                    </tr></thead>
                    <tbody>
                      {filtered.map(u => (
                        <tr key={u.id} onClick={() => setOpen(u)}>
                          <td style={{ fontWeight: 600 }}>{u.full_name || u.email}</td>
                          <td className="mono faint" style={{ fontSize: 12 }}>{u.email}</td>
                          <td><Pill tone={u.role === "admin" ? "pink" : u.role === "manager" ? "cyan" : "muted"}>{humanize(u.role)}</Pill></td>
                          <td><Pill tone={u.active ? "ok" : "danger"}><span className="dot"/>{u.active ? "Active" : "Inactive"}</Pill></td>
                          <td className="muted">{formatDateTime(u.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
            }
          </div>
        </div>
      </>}

      {tab === "logins" && <LoginHistoryPanel userRows={rows || []} />}
      {tab === "audit" && <AuditLogPanel userRows={rows || []} />}

      {open && <UserDrawer user={open} onClose={() => setOpen(null)} onSaved={() => { setOpen(null); refresh(); }} />}
      {inviting && <InviteUserDrawer onClose={() => setInviting(false)} onSaved={() => { setInviting(false); refresh(); }} />}
    </div>
  );
}

function LoginHistoryPanel({ userRows }) {
  const [logs, setLogs] = React.useState(null);

  React.useEffect(() => {
    const ctrl = new AbortController();
    AuditLog.logins(ctrl.signal)
      .then(r => setLogs(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setLogs([]));
    return () => ctrl.abort();
  }, []);

  const userById = React.useMemo(() => {
    const m = {};
    (userRows || []).forEach(u => { m[u.id] = u; });
    return m;
  }, [userRows]);

  if (logs === null) return <Spinner label="Loading login history…" />;
  if (logs.length === 0) return <EmptyState title="No logins recorded" hint="Sign-in events appear here automatically when users log in." />;

  return (
    <div className="card">
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr>
            <th>When</th><th>User</th><th>Email</th><th>Role</th>
          </tr></thead>
          <tbody>
            {logs.map(row => {
              const u = userById[row.changed_by] || {};
              return (
                <tr key={row.id}>
                  <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.changed_at)}</td>
                  <td style={{ fontWeight: 600 }}>{u.full_name || u.email || row.changed_by?.slice(0, 8) + '…'}</td>
                  <td className="mono faint" style={{ fontSize: 12 }}>{u.email || '—'}</td>
                  <td>{u.role ? <Pill tone={u.role === 'admin' ? 'pink' : u.role === 'manager' ? 'cyan' : 'muted'}>{humanize(u.role)}</Pill> : '—'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AuditLogPanel({ userRows }) {
  const [logs, setLogs] = React.useState(null);
  const [expanded, setExpanded] = React.useState(null);
  const [sort, setSort] = React.useState({ col: 'when', dir: 'desc' });
  const [whoFilter, setWhoFilter] = React.useState('');
  const [tableFilter, setTableFilter] = React.useState('');

  React.useEffect(() => {
    const ctrl = new AbortController();
    AuditLog.list(ctrl.signal)
      .then(r => setLogs(Array.isArray(r) ? r : (r.data || [])))
      .catch(() => setLogs([]));
    return () => ctrl.abort();
  }, []);

  const userById = React.useMemo(() => {
    const m = {};
    (userRows || []).forEach(u => { m[u.id] = u.full_name || u.email || u.id; });
    return m;
  }, [userRows]);

  function actionTone(a) {
    if (a === "insert") return "cyan";
    if (a === "update") return "warn";
    if (a === "delete") return "danger";
    if (a === "login")  return "ok";
    return "muted";
  }

  function diffKeys(oldData, newData) {
    const keys = new Set([...Object.keys(oldData || {}), ...Object.keys(newData || {})]);
    return [...keys].filter(k => {
      const ov = JSON.stringify((oldData || {})[k]);
      const nv = JSON.stringify((newData || {})[k]);
      return ov !== nv;
    });
  }

  function toggleSort(col) {
    setSort(prev => ({ col, dir: prev.col === col && prev.dir === 'asc' ? 'desc' : 'asc' }));
  }

  const uniqueUsers = React.useMemo(() => {
    const seen = new Set();
    const users = [];
    (logs || []).forEach(r => {
      if (r.changed_by && !seen.has(r.changed_by)) {
        seen.add(r.changed_by);
        users.push({ id: r.changed_by, name: userById[r.changed_by] || r.changed_by.slice(0, 8) + '…' });
      }
    });
    return users.sort((a, b) => a.name.localeCompare(b.name));
  }, [logs, userById]);

  const uniqueTables = React.useMemo(() => {
    const seen = new Set();
    (logs || []).forEach(r => { if (r.table_name) seen.add(r.table_name); });
    return [...seen].sort();
  }, [logs]);

  const sortedLogs = React.useMemo(() => {
    let list = logs || [];
    if (whoFilter) list = list.filter(r => r.changed_by === whoFilter);
    if (tableFilter) list = list.filter(r => r.table_name === tableFilter);
    return [...list].sort((a, b) => {
      let va = '', vb = '';
      if (sort.col === 'when')   { va = a.changed_at || ''; vb = b.changed_at || ''; }
      else if (sort.col === 'table')  { va = a.table_name || ''; vb = b.table_name || ''; }
      else if (sort.col === 'action') { va = a.action || ''; vb = b.action || ''; }
      else if (sort.col === 'who') {
        va = a.changed_by ? (userById[a.changed_by] || a.changed_by) : '';
        vb = b.changed_by ? (userById[b.changed_by] || b.changed_by) : '';
      }
      if (va < vb) return sort.dir === 'asc' ? -1 : 1;
      if (va > vb) return sort.dir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [logs, sort, whoFilter, tableFilter, userById]);

  const sortArrow = (col) => sort.col === col ? (sort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  if (logs === null) return <Spinner label="Loading audit log…" />;
  if (logs.length === 0) return <EmptyState title="No audit entries" hint="Logins and changes to orders, contacts, production, and expenses will appear here." />;

  return (
    <div>
      <div className="filter-bar" style={{ marginBottom: 10 }}>
        <select className="select" style={{ width: 'auto', minWidth: 160 }} value={tableFilter} onChange={e => setTableFilter(e.target.value)}>
          <option value="">All tables</option>
          {uniqueTables.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="select" style={{ width: 'auto', minWidth: 160 }} value={whoFilter} onChange={e => setWhoFilter(e.target.value)}>
          <option value="">All users</option>
          {uniqueUsers.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        {(whoFilter || tableFilter) && (
          <button className="btn btn-ghost btn-sm" onClick={() => { setWhoFilter(''); setTableFilter(''); }}>Clear filters</button>
        )}
        <span className="spacer" />
        <span className="faint" style={{ fontSize: 12 }}>{sortedLogs.length} entries</span>
      </div>
      <div className="card">
      <div className="tbl-wrap">
        <table className="tbl">
          <thead><tr>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('when')}>When{sortArrow('when')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('table')}>Table{sortArrow('table')}</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('action')}>Action{sortArrow('action')}</th>
            <th>Record ID</th>
            <th style={{ cursor: 'pointer', userSelect: 'none' }} onClick={() => toggleSort('who')}>Who{sortArrow('who')}</th>
          </tr></thead>
          <tbody>
            {sortedLogs.map(row => {
              const isExpanded = expanded === row.id;
              const changed = row.action === "update" ? diffKeys(row.old_data, row.new_data) : [];
              return (
                <React.Fragment key={row.id}>
                  <tr style={{ cursor: "pointer" }} onClick={() => setExpanded(isExpanded ? null : row.id)}>
                    <td className="muted" style={{ whiteSpace: "nowrap" }}>{formatDateTime(row.changed_at)}</td>
                    <td className="mono" style={{ fontSize: 12 }}>{row.table_name}</td>
                    <td><Pill tone={actionTone(row.action)}>{row.action}</Pill></td>
                    <td className="mono faint" style={{ fontSize: 11 }}>{row.action === "login" ? "—" : (row.record_id || "").slice(0, 8) + "…"}</td>
                    <td className="muted">{row.changed_by ? (userById[row.changed_by] || row.changed_by.slice(0, 8) + "…") : <span className="faint">system</span>}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={5} style={{ background: "var(--surface-2)", padding: "12px 16px" }}>
                        {row.action === "login" && (
                          <div className="faint" style={{ fontSize: 12 }}>User signed in.</div>
                        )}
                        {row.action === "update" && changed.length > 0 && (
                          <div style={{ marginBottom: 10 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text-muted)", marginBottom: 6 }}>CHANGED FIELDS</div>
                            <table style={{ fontSize: 12, borderCollapse: "collapse", width: "100%" }}>
                              <thead><tr>
                                <th style={{ textAlign: "left", padding: "2px 8px 2px 0", color: "var(--text-muted)" }}>Field</th>
                                <th style={{ textAlign: "left", padding: "2px 8px 2px 0", color: "var(--text-muted)" }}>Before</th>
                                <th style={{ textAlign: "left", padding: "2px 8px 2px 0", color: "var(--text-muted)" }}>After</th>
                              </tr></thead>
                              <tbody>
                                {changed.map(k => (
                                  <tr key={k}>
                                    <td className="mono" style={{ padding: "2px 8px 2px 0", fontWeight: 600 }}>{k}</td>
                                    <td className="mono" style={{ padding: "2px 8px 2px 0", color: "var(--danger)", textDecoration: "line-through" }}>
                                      {JSON.stringify((row.old_data || {})[k])}
                                    </td>
                                    <td className="mono" style={{ padding: "2px 8px 2px 0", color: "var(--ok)" }}>
                                      {JSON.stringify((row.new_data || {})[k])}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                        {(row.old_data || row.new_data) && (
                          <details style={{ fontSize: 11 }}>
                            <summary style={{ cursor: "pointer", color: "var(--text-muted)", marginBottom: 4 }}>Raw data</summary>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
                              {row.old_data && (
                                <div>
                                  <div style={{ fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>Before</div>
                                  <pre style={{ margin: 0, fontSize: 10, background: "var(--surface-3)", padding: 8, borderRadius: 6, overflow: "auto", maxHeight: 200 }}>{JSON.stringify(row.old_data, null, 2)}</pre>
                                </div>
                              )}
                              {row.new_data && (
                                <div>
                                  <div style={{ fontWeight: 600, color: "var(--text-muted)", marginBottom: 4 }}>After</div>
                                  <pre style={{ margin: 0, fontSize: 10, background: "var(--surface-3)", padding: 8, borderRadius: 6, overflow: "auto", maxHeight: 200 }}>{JSON.stringify(row.new_data, null, 2)}</pre>
                                </div>
                              )}
                            </div>
                          </details>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    </div>
  );
}

function UserDrawer({ user, onClose, onSaved }) {
  const toast = useToast();
  const { profile } = useAuth();
  const [f, setF] = React.useState({ full_name: user.full_name || "", role: user.role || "staff", active: user.active !== false });
  const [busy, setBusy] = React.useState(false);
  const [confirmDelete, setConfirmDelete] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const isSelf = profile?.id === user.id;

  async function save() {
    setBusy(true);
    const res = await Users.update(user.id, { full_name: f.full_name, role: f.role, active: f.active });
    if (!res.error) {
      const wasActive = user.active !== false;
      if (wasActive && !f.active) {
        await Users.revokeSession(user.id);
      }
      toast.push({ tone: "ok", title: "User updated" });
      onSaved();
    }
    setBusy(false);
  }

  async function doDelete() {
    setBusy(true);
    const res = await Users.delete(user.id);
    setBusy(false);
    if (res.error) {
      toast.push({ tone: "danger", title: "Delete failed", msg: res.error.message });
      setConfirmDelete(false);
      return;
    }
    toast.push({ tone: "ok", title: "User deleted" });
    onSaved();
  }

  return (
    <>
    <Drawer
      title={`Edit · ${user.full_name || user.email}`}
      subtitle={user.email}
      onClose={onClose}
      footer={<>
        <button className="btn btn-danger" onClick={() => setConfirmDelete(true)} disabled={busy || isSelf} title={isSelf ? "Cannot delete your own account" : "Delete user"}>
          {I.trash} Delete
        </button>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={busy}>{busy ? "Saving…" : <>{I.check} Save</>}</button>
      </>}
    >
      <Field label="Full name">
        <input className="input" value={f.full_name} onChange={e => set("full_name", e.target.value)} />
      </Field>
      <Field label="Email" help="Change email via Supabase dashboard.">
        <input className="input" value={user.email} disabled style={{ opacity: 0.5 }} />
      </Field>
      <Field label="Role">
        <EnumSelect value={f.role} onChange={v => set("role", v)} options={ENUM_OPTIONS.user_role} />
      </Field>
      <Field label="Active">
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <input type="checkbox" checked={f.active} onChange={e => set("active", e.target.checked)} />
          User can log in and access the app
        </label>
      </Field>
    </Drawer>
    {confirmDelete && (
      <ConfirmModal
        title="Delete user"
        message={`Permanently delete ${user.full_name || user.email}? This removes their Supabase account and cannot be undone.`}
        confirmLabel="Delete permanently"
        danger
        onConfirm={doDelete}
        onClose={() => setConfirmDelete(false)}
      />
    )}
    </>
  );
}

function InviteUserDrawer({ onClose, onSaved }) {
  const toast = useToast();
  const [f, setF] = React.useState({ email: "", full_name: "", role: "staff" });
  const [busy, setBusy] = React.useState(false);
  const [sent, setSent] = React.useState(false);
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  async function invite() {
    if (!f.email?.trim()) return;
    setBusy(true);
    const res = await Users.invite(f.email.trim(), f.full_name.trim(), f.role);
    setBusy(false);
    if (res.error) {
      reportError({ code: "WYF-DB-107", where: "Invite user", message: res.error.message || "Invite failed." });
      return;
    }
    setSent(true);
    toast.push({ tone: "ok", title: "Invite sent!", msg: `Magic link sent to ${f.email}` });
    setTimeout(() => { onSaved(); }, 1500);
  }

  return (
    <Drawer
      title="Invite new user"
      subtitle="They'll receive a magic link to set their password."
      onClose={onClose}
      footer={<>
        <span className="spacer"/>
        <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={invite} disabled={busy || !f.email || sent}>
          {busy ? "Sending…" : sent ? "Sent!" : <>{I.plus} Send invite</>}
        </button>
      </>}
    >
      <Field label="Email" required>
        <input className="input" type="email" value={f.email} onChange={e => set("email", e.target.value)} placeholder="user@example.com" />
      </Field>
      <Field label="Full name">
        <input className="input" value={f.full_name} onChange={e => set("full_name", e.target.value)} placeholder="e.g. Maria Santos" />
      </Field>
      <Field label="Role">
        <EnumSelect value={f.role} onChange={v => set("role", v)} options={ENUM_OPTIONS.user_role} />
      </Field>
      <div style={{ marginTop: 12, padding: "10px 12px", background: "var(--surface-2)", borderRadius: 10, fontSize: 12, color: "var(--text-muted)" }}>
        The user will receive an email with a login link. Their role can be changed later from this page.
      </div>
    </Drawer>
  );
}

export {
  ProductionView, InventoryView, ProductsView, ExpensesView, UsersView,
};
